import { showToast } from './Toast.js';

// ── Types ─────────────────────────────────────────────────────────────────────
export type DocType =
  | 'government_id'
  | 'passport'
  | 'business_license'
  | 'proof_of_address'
  | 'bank_statement'
  | 'signed_agreement'
  | 'other';

export interface IdDocument {
  id: string;
  businessId: string;
  docType: DocType;
  label: string;
  fileName: string;
  fileSize: number;
  dataUri: string;          // base64 data URI for preview
  uploadedAt: string;
  expiryDate?: string;
  status: 'pending' | 'verified' | 'rejected';
  adminNote?: string;
}

const DOC_TYPE_LABEL: Record<DocType, string> = {
  government_id:    'Government-Issued Photo ID',
  passport:         'Passport',
  business_license: 'Business License / Registration',
  proof_of_address: 'Proof of Address',
  bank_statement:   'Bank Statement / Financial Records',
  signed_agreement: 'Signed Service Agreement',
  other:            'Other Document',
};

const DOC_TYPE_ICON: Record<DocType, string> = {
  government_id:    'bi-person-vcard',
  passport:         'bi-globe2',
  business_license: 'bi-building-check',
  proof_of_address: 'bi-house-check',
  bank_statement:   'bi-bank',
  signed_agreement: 'bi-pen',
  other:            'bi-file-earmark',
};

// ── Store ─────────────────────────────────────────────────────────────────────
function idDocStore(): Record<string, IdDocument[]> {
  const w = window as unknown as Record<string, unknown>;
  if (!w['__preuve_id_docs']) w['__preuve_id_docs'] = {};
  return w['__preuve_id_docs'] as Record<string, IdDocument[]>;
}

export function getDocsForBusiness(businessId: string): IdDocument[] {
  return idDocStore()[businessId] ?? [];
}

export function addDocument(doc: IdDocument): void {
  const store = idDocStore();
  if (!store[doc.businessId]) store[doc.businessId] = [];
  store[doc.businessId]!.push(doc);
}

export function removeDocument(businessId: string, docId: string): void {
  const store = idDocStore();
  if (store[businessId]) {
    store[businessId] = store[businessId]!.filter((d) => d.id !== docId);
  }
}

export function updateDocStatus(
  businessId: string,
  docId: string,
  status: IdDocument['status'],
  adminNote?: string
): void {
  const store = idDocStore();
  const docs = store[businessId];
  if (!docs) return;
  const doc = docs.find((d) => d.id === docId);
  if (doc) { doc.status = status; if (adminNote !== undefined) doc.adminNote = adminNote; }
}

export type VerificationSummary = 'unverified' | 'pending' | 'partial' | 'verified';

export function getVerificationSummary(businessId: string): VerificationSummary {
  const docs = getDocsForBusiness(businessId);
  if (!docs.length) return 'unverified';
  if (docs.every((d) => d.status === 'verified')) return 'verified';
  if (docs.some((d) => d.status === 'verified')) return 'partial';
  if (docs.some((d) => d.status === 'pending')) return 'pending';
  return 'unverified';
}

// ── Render (Settings section) ─────────────────────────────────────────────────
export function renderIdentityVerification(container: HTMLElement, businessId: string): void {
  function rebuild(): void {
    const docs = getDocsForBusiness(businessId);
    const summary = getVerificationSummary(businessId);

    const summaryColor = { unverified: '#ef4444', pending: '#f59e0b', partial: '#3b82f6', verified: '#10b981' }[summary];
    const summaryIcon  = { unverified: 'bi-x-circle', pending: 'bi-hourglass-split', partial: 'bi-shield-half', verified: 'bi-shield-check' }[summary];
    const summaryLabel = { unverified: 'Not Verified', pending: 'Pending Review', partial: 'Partially Verified', verified: 'Fully Verified' }[summary];

    container.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;flex-wrap:wrap;gap:10px">
        <div>
          <h2 style="font-size:14px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">
            <i class="bi bi-shield-lock"></i> Identity &amp; Document Verification
          </h2>
          <p style="font-size:12px;color:var(--text-muted);margin-top:4px">
            Upload government IDs, passport, business license, or other compliance documents.
          </p>
        </div>
        <div style="display:flex;align-items:center;gap:8px">
          <span style="display:flex;align-items:center;gap:6px;background:${summaryColor}18;border:1px solid ${summaryColor}44;border-radius:20px;padding:5px 12px;font-size:12px;font-weight:700;color:${summaryColor}">
            <i class="bi ${summaryIcon}"></i> ${summaryLabel}
          </span>
          <button class="btn btn-gold btn-sm" id="upload-doc-btn">
            <i class="bi bi-cloud-upload"></i> Upload Document
          </button>
        </div>
      </div>

      ${docs.length === 0 ? `
        <div class="empty-state" style="padding:40px 20px;border:2px dashed var(--border);border-radius:var(--radius-md)">
          <div class="empty-state-icon" style="font-size:36px"><i class="bi bi-file-earmark-person"></i></div>
          <div class="empty-state-title" style="font-size:14px">No documents uploaded</div>
          <div class="empty-state-desc" style="font-size:12px">Upload a government ID, passport, or business license to verify your account.</div>
          <button class="btn btn-primary btn-sm" style="margin-top:16px" id="upload-doc-btn-empty">
            <i class="bi bi-cloud-upload"></i> Upload First Document
          </button>
        </div>
      ` : `
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:14px" id="doc-grid">
          ${docs.map((doc) => renderDocCard(doc)).join('')}
        </div>
      `}

      <!-- Upload Modal -->
      <div id="upload-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:2000;align-items:center;justify-content:center">
        <div class="card" style="width:520px;max-height:90vh;overflow-y:auto">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
            <h3 style="font-size:15px;font-weight:700"><i class="bi bi-cloud-upload" style="color:var(--brand-gold)"></i> Upload Verification Document</h3>
            <button class="btn btn-ghost btn-sm" id="close-upload-modal"><i class="bi bi-x-lg"></i></button>
          </div>
          <div class="form-row">
            <label>Document Type *</label>
            <select id="doc-type-select">
              ${Object.entries(DOC_TYPE_LABEL).map(([val, lbl]) => `<option value="${val}">${lbl}</option>`).join('')}
            </select>
          </div>
          <div class="form-row">
            <label>Expiry Date <span style="font-weight:400;color:var(--text-muted)">(optional — passports, IDs)</span></label>
            <input type="date" id="doc-expiry" />
          </div>
          <!-- Drag-drop upload zone -->
          <div id="drop-zone" class="doc-upload-zone" style="margin-bottom:16px">
            <i class="bi bi-file-earmark-image" style="font-size:36px;color:var(--text-muted);margin-bottom:10px"></i>
            <div style="font-size:14px;font-weight:600;color:var(--text-secondary)">Drag &amp; drop your file here</div>
            <div style="font-size:12px;color:var(--text-muted);margin:6px 0">or</div>
            <button class="btn btn-ghost btn-sm" id="browse-btn" type="button"><i class="bi bi-folder2-open"></i> Browse Files</button>
            <div style="font-size:11px;color:var(--text-muted);margin-top:8px">Accepted: JPG, PNG, WebP, PDF · Max 10 MB</div>
          </div>
          <input type="file" id="file-input" accept="image/jpeg,image/png,image/webp,application/pdf" style="display:none" />
          <!-- Preview area -->
          <div id="file-preview" style="display:none;margin-bottom:16px">
            <div style="display:flex;align-items:center;gap:12px;background:var(--bg-tertiary);border-radius:var(--radius-sm);padding:12px">
              <div id="preview-thumb" style="width:56px;height:56px;border-radius:6px;overflow:hidden;background:var(--bg-primary);flex-shrink:0;display:flex;align-items:center;justify-content:center"></div>
              <div style="flex:1;min-width:0">
                <div id="preview-name" style="font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"></div>
                <div id="preview-size" style="font-size:11px;color:var(--text-muted);margin-top:2px"></div>
              </div>
              <button class="btn btn-ghost btn-sm" id="remove-file-btn" title="Remove"><i class="bi bi-x"></i></button>
            </div>
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button class="btn btn-ghost" id="cancel-upload-btn">Cancel</button>
            <button class="btn btn-gold" id="confirm-upload-btn" disabled>
              <i class="bi bi-cloud-upload"></i> Upload Document
            </button>
          </div>
        </div>
      </div>

      <!-- Preview Modal -->
      <div id="doc-preview-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:2100;align-items:center;justify-content:center;flex-direction:column;gap:12px">
        <div style="display:flex;justify-content:flex-end;width:min(90vw,760px)">
          <button class="btn btn-ghost btn-sm" id="close-preview-modal" style="color:#fff;border-color:#ffffff44"><i class="bi bi-x-lg"></i> Close</button>
        </div>
        <img id="preview-modal-img" src="" style="max-width:min(90vw,760px);max-height:75vh;border-radius:var(--radius-md);object-fit:contain;display:none" />
        <iframe id="preview-modal-pdf" src="" style="width:min(90vw,760px);height:75vh;border:none;border-radius:var(--radius-md);display:none"></iframe>
        <div id="preview-modal-label" style="font-size:13px;color:#94a3b8"></div>
      </div>
    `;

    wireUpload(businessId, rebuild);
    wireDocActions(businessId, docs, rebuild);
  }

  rebuild();
}

function renderDocCard(doc: IdDocument): string {
  const statusColor = { pending: '#f59e0b', verified: '#10b981', rejected: '#ef4444' }[doc.status];
  const statusLabel = { pending: 'Pending Review', verified: 'Verified', rejected: 'Rejected' }[doc.status];
  const statusIcon  = { pending: 'bi-hourglass-split', verified: 'bi-check-circle-fill', rejected: 'bi-x-circle-fill' }[doc.status];
  const isImage = doc.dataUri.startsWith('data:image');
  const thumb = isImage
    ? `<img src="${doc.dataUri}" style="width:100%;height:100%;object-fit:cover" />`
    : `<i class="bi bi-file-earmark-pdf" style="font-size:28px;color:#ef4444"></i>`;

  return `
    <div class="doc-card" data-doc-id="${doc.id}">
      <div class="doc-thumb">
        ${thumb}
      </div>
      <div class="doc-card-body">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px">
          <div>
            <div style="font-size:13px;font-weight:700;color:var(--text-primary)">
              <i class="bi ${DOC_TYPE_ICON[doc.docType]}" style="color:var(--brand-gold)"></i>
              ${DOC_TYPE_LABEL[doc.docType]}
            </div>
            <div style="font-size:11px;color:var(--text-muted);margin-top:3px">
              ${doc.fileName} · ${formatFileSize(doc.fileSize)}
            </div>
            ${doc.expiryDate ? `<div style="font-size:11px;color:var(--text-muted)"><i class="bi bi-calendar3"></i> Expires ${doc.expiryDate}</div>` : ''}
          </div>
          <span style="flex-shrink:0;display:inline-flex;align-items:center;gap:4px;background:${statusColor}18;border:1px solid ${statusColor}44;border-radius:20px;padding:3px 10px;font-size:10px;font-weight:700;color:${statusColor}">
            <i class="bi ${statusIcon}"></i> ${statusLabel}
          </span>
        </div>
        ${doc.adminNote ? `<div style="margin-top:8px;padding:6px 10px;background:var(--bg-tertiary);border-radius:var(--radius-sm);font-size:11px;color:var(--text-muted)"><i class="bi bi-chat-text"></i> Admin: ${escHtml(doc.adminNote)}</div>` : ''}
        <div style="display:flex;gap:6px;margin-top:10px">
          <button class="btn btn-ghost btn-sm doc-preview-btn" data-doc-id="${doc.id}" style="flex:1"><i class="bi bi-eye"></i> View</button>
          ${doc.status === 'pending' ? `<button class="btn btn-ghost btn-sm doc-delete-btn" data-doc-id="${doc.id}" style="color:var(--danger)"><i class="bi bi-trash3"></i></button>` : ''}
        </div>
        <div style="font-size:10px;color:var(--text-muted);margin-top:6px">
          Uploaded ${new Date(doc.uploadedAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  `;
}

function wireUpload(businessId: string, rebuild: () => void): void {
  let selectedFile: File | null = null;
  let selectedDataUri = '';

  const modal = document.getElementById('upload-modal')!;
  const fileInput = document.getElementById('file-input') as HTMLInputElement;
  const dropZone = document.getElementById('drop-zone')!;
  const filePreview = document.getElementById('file-preview')!;
  const confirmBtn = document.getElementById('confirm-upload-btn') as HTMLButtonElement;

  const openModal = (): void => { modal.style.display = 'flex'; selectedFile = null; selectedDataUri = ''; filePreview.style.display = 'none'; confirmBtn.disabled = true; };
  document.getElementById('upload-doc-btn')?.addEventListener('click', openModal);
  document.getElementById('upload-doc-btn-empty')?.addEventListener('click', openModal);
  document.getElementById('close-upload-modal')?.addEventListener('click', () => { modal.style.display = 'none'; });
  document.getElementById('cancel-upload-btn')?.addEventListener('click', () => { modal.style.display = 'none'; });
  document.getElementById('browse-btn')?.addEventListener('click', () => fileInput.click());
  document.getElementById('remove-file-btn')?.addEventListener('click', () => {
    selectedFile = null; selectedDataUri = '';
    filePreview.style.display = 'none';
    confirmBtn.disabled = true;
    fileInput.value = '';
  });

  const handleFile = (file: File): void => {
    if (file.size > 10 * 1024 * 1024) { showToast('File too large — max 10 MB', 'error'); return; }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) { showToast('Unsupported file type', 'error'); return; }
    selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e): void => {
      selectedDataUri = e.target!.result as string;
      const thumb = document.getElementById('preview-thumb')!;
      if (file.type.startsWith('image/')) {
        thumb.innerHTML = `<img src="${selectedDataUri}" style="width:100%;height:100%;object-fit:cover" />`;
      } else {
        thumb.innerHTML = `<i class="bi bi-file-earmark-pdf" style="font-size:26px;color:#ef4444"></i>`;
      }
      document.getElementById('preview-name')!.textContent = file.name;
      document.getElementById('preview-size')!.textContent = formatFileSize(file.size);
      filePreview.style.display = 'block';
      confirmBtn.disabled = false;
    };
    reader.readAsDataURL(file);
  };

  fileInput.addEventListener('change', () => { if (fileInput.files?.[0]) handleFile(fileInput.files[0]); });

  dropZone.addEventListener('dragover', (e) => { e.preventDefault(); dropZone.classList.add('dragover'); });
  dropZone.addEventListener('dragleave', () => dropZone.classList.remove('dragover'));
  dropZone.addEventListener('drop', (e) => {
    e.preventDefault(); dropZone.classList.remove('dragover');
    if (e.dataTransfer?.files?.[0]) handleFile(e.dataTransfer.files[0]);
  });

  confirmBtn.addEventListener('click', () => {
    if (!selectedFile || !selectedDataUri) return;
    const docType = (document.getElementById('doc-type-select') as HTMLSelectElement).value as DocType;
    const expiry = (document.getElementById('doc-expiry') as HTMLInputElement).value;
    const doc: IdDocument = {
      id: crypto.randomUUID(),
      businessId,
      docType,
      label: DOC_TYPE_LABEL[docType],
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      dataUri: selectedDataUri,
      uploadedAt: new Date().toISOString(),
      expiryDate: expiry || undefined,
      status: 'pending',
    };
    addDocument(doc);
    modal.style.display = 'none';
    showToast('Document uploaded — awaiting admin review', 'success');
    rebuild();
  });
}

function wireDocActions(businessId: string, docs: IdDocument[], rebuild: () => void): void {
  document.querySelectorAll('.doc-preview-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-doc-id')!;
      const doc = docs.find((d) => d.id === id);
      if (!doc) return;
      const previewModal = document.getElementById('doc-preview-modal')!;
      const img = document.getElementById('preview-modal-img') as HTMLImageElement;
      const pdf = document.getElementById('preview-modal-pdf') as HTMLIFrameElement;
      const label = document.getElementById('preview-modal-label')!;

      if (doc.dataUri.startsWith('data:image')) {
        img.src = doc.dataUri; img.style.display = 'block'; pdf.style.display = 'none';
      } else {
        pdf.src = doc.dataUri; pdf.style.display = 'block'; img.style.display = 'none';
      }
      label.textContent = `${DOC_TYPE_LABEL[doc.docType]} — ${doc.fileName}`;
      previewModal.style.display = 'flex';
    });
  });

  document.querySelectorAll('.doc-delete-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!confirm('Remove this document?')) return;
      removeDocument(businessId, btn.getAttribute('data-doc-id')!);
      showToast('Document removed', 'info');
      rebuild();
    });
  });

  document.getElementById('close-preview-modal')?.addEventListener('click', () => {
    document.getElementById('doc-preview-modal')!.style.display = 'none';
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
